import type { HelpGuide } from '../types';

export const fixedassetGuide: HelpGuide = {
  key: 'fixedasset',
  title: 'สินทรัพย์ถาวร (Fixed Asset Register)',
  subtitle: 'เมนู: บัญชีแยกประเภท ▸ สินทรัพย์ถาวร',
  updated: 'ปรับปรุง 20 มิ.ย. 2569',
  docxUrl: '/help/fixedasset/คู่มือการใช้งาน_สินทรัพย์ถาวร_DCC-ERP.docx',
  sections: [
    {
      id: 'overview',
      heading: '1. หน้านี้คืออะไร และใช้ทำอะไร',
      blocks: [
        {
          kind: 'para',
          text: 'หน้า “สินทรัพย์ถาวร” คือทะเบียนคุมสินทรัพย์ถาวร (ที่ดิน อาคาร เครื่องจักร ยานพาหนะ คอมพิวเตอร์ ฯลฯ) ใช้ดูแลสินทรัพย์ตลอดวงจรชีวิต ตั้งแต่ได้มา คิดค่าเสื่อมราคาทุกเดือน ไปจนถึงจำหน่าย/ปลดระวาง พร้อมลงบัญชีอัตโนมัติและกระทบยอดกับบัญชีแยกประเภท (GL)',
        },
        {
          kind: 'para',
          text: 'วงจรชีวิต: ได้มา/ลงทะเบียน → คิดค่าเสื่อมราคา (รายเดือน) → จำหน่าย/ปลดระวาง หน้านี้แบ่งเป็น 4 แท็บ: ทะเบียนสินทรัพย์ · ตารางค่าเสื่อมราคา · การเคลื่อนไหว/โอน-จำหน่าย · กระทบยอด GL',
        },
        {
          kind: 'image',
          src: '/help/fixedasset/01-register.png',
          caption: 'ภาพรวม — การ์ด KPI 4 ใบ การ์ดสรุปตามหมวด 6 หมวด และตารางทะเบียนสินทรัพย์',
        },
      ],
    },
    {
      id: 'anatomy',
      heading: '2. ส่วนประกอบของหน้าจอ (แท็บทะเบียน)',
      blocks: [
        {
          kind: 'bullets',
          items: [
            { lead: 'นำเข้า — ', text: 'นำเข้าสินทรัพย์ทีละหลายรายการจากไฟล์ Excel/CSV' },
            { lead: 'ส่งออก — ', text: 'ส่งออกรายการสินทรัพย์ที่เห็นบนหน้าจอเป็น Excel' },
            {
              lead: 'รันค่าเสื่อม — ',
              text: 'คำนวณและลงบัญชีค่าเสื่อมราคาของงวดที่ระบุ (กรอกงวด YYYYMM)',
            },
            { lead: 'ลงทะเบียนสินทรัพย์ — ', text: 'เปิดฟอร์มลงทะเบียนสินทรัพย์ใหม่ (ปุ่มสีแดง)' },
          ],
        },
        {
          kind: 'para',
          text: 'การ์ด KPI 4 ใบ: ราคาทุนรวม · ค่าเสื่อมสะสม · มูลค่าทางบัญชีสุทธิ (NBV) · ค่าเสื่อม YTD ใต้การ์ดคือสรุปตามหมวด 6 หมวด พร้อมแถบสัดส่วน',
        },
        {
          kind: 'para',
          text: 'ตารางทะเบียนกรองได้ด้วย ค้นหา/หมวด/สาขา/หน่วยงาน/สถานะ คอลัมน์: รหัสสินทรัพย์ · ชื่อ · หมวด · หน่วยงาน · วันที่ได้มา · ราคาทุน · อายุใช้งาน · ค่าเสื่อมสะสม · NBV · สถานะ · การจัดการ (ปุ่มจำหน่าย)',
        },
      ],
    },
    {
      id: 'register',
      heading: '3. การลงทะเบียนสินทรัพย์ใหม่',
      blocks: [
        {
          kind: 'para',
          text: 'กดปุ่ม ลงทะเบียนสินทรัพย์ ระบบเปิดฟอร์ม 2 ขั้นตอน ขั้นที่ 1 กรอกข้อมูลและบันทึก JV ตั้งต้น:',
        },
        {
          kind: 'steps',
          items: [
            {
              title: 'หมวดสินทรัพย์',
              text: 'เลือกหมวด ระบบเติม “อายุใช้งาน” และ “มูลค่าซาก” เริ่มต้น พร้อมผูกผังบัญชีอัตโนมัติ',
            },
            {
              title: 'รหัสภายใน (Asset tag) + ชื่อสินทรัพย์',
              text: 'รหัสอ้างอิงและชื่อที่จดจำง่าย',
            },
            {
              title: 'วันที่ได้มา / วันเริ่มคิดค่าเสื่อม',
              text: 'วันเริ่มคิดค่าเสื่อมผูกกับวันที่ได้มาอัตโนมัติ ปรับเองได้',
            },
            {
              title: 'ราคาทุน / มูลค่าซาก / อายุใช้งาน (เดือน)',
              text: 'วิธีคิดค่าเสื่อมล็อกไว้ที่เส้นตรง (Straight-line)',
            },
            {
              title: 'การชำระเงิน',
              text: 'เลือกแหล่งเงิน: ซื้อเป็นเงินเชื่อ (ตั้งเจ้าหนี้) หรือ จ่ายผ่านธนาคาร (หักเงินฝาก)',
            },
          ],
        },
        {
          kind: 'para',
          text: 'ขณะกรอก ระบบแสดง “สรุปการคำนวณ” (ฐานคิดค่าเสื่อม/ต่อเดือน/ต่อปี) และพรีวิว JV (Dr บัญชีสินทรัพย์ / Cr แหล่งเงิน) แบบเรียลไทม์ แล้วกด บันทึก & ไปต่อ',
        },
        {
          kind: 'image',
          src: '/help/fixedasset/02-register-modal.png',
          caption:
            'ฟอร์มลงทะเบียน — พรีวิวการคำนวณค่าเสื่อมและ JV ตั้งต้น (Dr เครื่องจักร / Cr เจ้าหนี้)',
        },
        {
          kind: 'para',
          text: 'ขั้นที่ 2 — แนบรูป + ไฟล์ประกอบ: หลังบันทึกสำเร็จ ระบบยืนยันและเปิดหน้าแนบไฟล์ ลากรูป/เอกสาร (สูงสุด 10MB/ไฟล์) มาผูกกับสินทรัพย์ แล้วกด เสร็จสิ้น',
        },
        {
          kind: 'image',
          src: '/help/fixedasset/03-attachments.png',
          caption: 'ขั้นตอน 2/2 — ยืนยันบันทึกสำเร็จ และพื้นที่ลากวางรูป/ไฟล์ประกอบ',
        },
      ],
    },
    {
      id: 'receive',
      heading: '4. การรับสินทรัพย์เข้าทะเบียน (จากบิล/ใบสำคัญ)',
      blocks: [
        {
          kind: 'para',
          text: 'เมื่อซื้อสินทรัพย์ผ่านบิลเจ้าหนี้ (AP) หรือใบสำคัญทั่วไป รายการจะพักที่บัญชีพักสินทรัพย์ (FA_CLEARING) และรอนำเข้าทะเบียน ดูได้ที่แท็บ การเคลื่อนไหว ในกล่อง “การได้มาของสินทรัพย์”',
        },
        {
          kind: 'image',
          src: '/help/fixedasset/06-movement.png',
          caption:
            'แท็บการเคลื่อนไหว — รายการรอรับเข้าทะเบียน (ปุ่ม รับเข้าทะเบียน), การจำหน่าย/โอน YTD และประวัติ',
        },
        {
          kind: 'para',
          text: 'กดปุ่ม รับเข้าทะเบียน ที่แถวรายการ ระบบแสดงข้อมูลจากเอกสารต้นทาง (เลขที่ ราคาทุน แหล่งที่มา) แบบอ่านอย่างเดียว เหลือกรอกหมวด/รหัส/อายุ/มูลค่าซาก แล้วกด รับเข้าทะเบียน เพื่อแปลงเป็นสินทรัพย์ถาวร',
        },
        {
          kind: 'image',
          src: '/help/fixedasset/04-receive.png',
          caption:
            'หน้าต่างรับเข้าทะเบียน — ข้อมูลต้นทาง (อ่านอย่างเดียว) + ช่องกรอกหมวด/รหัส/อายุ',
        },
      ],
    },
    {
      id: 'depreciation',
      heading: '5. ค่าเสื่อมราคา',
      blocks: [
        {
          kind: 'para',
          text: 'แท็บ ตารางค่าเสื่อมราคา แสดงการ์ดฐานคิดค่าเสื่อม (ราคาทุน/มูลค่าซาก/ฐาน/ต่อปี/NBV) และตารางคำนวณค่าเสื่อมรายเดือนของสินทรัพย์ที่เลือก โดยแต่ละงวดแสดง ค่าเสื่อมสะสม(ยกมา) · งวดนี้ · ยกไป · NBV · เลขที่ JV · สถานะ',
        },
        {
          kind: 'image',
          src: '/help/fixedasset/05-depreciation.png',
          caption: 'ตารางค่าเสื่อมราคา — การ์ดฐานคิดค่าเสื่อม และตารางคำนวณรายเดือน 12 งวดถัดไป',
        },
        {
          kind: 'para',
          text: 'การลงบัญชีค่าเสื่อมประจำงวด: กดปุ่ม รันค่าเสื่อม มุมขวาบน กรอกงวด (เช่น 202606) ระบบคำนวณและโพสต์ใบสำคัญค่าเสื่อมของงวดนั้น (กันการรันซ้ำงวดเดิม)',
        },
      ],
    },
    {
      id: 'disposal',
      heading: '6. การจำหน่าย / ปลดระวางสินทรัพย์',
      blocks: [
        {
          kind: 'para',
          text: 'ที่ตารางทะเบียน คอลัมน์ การจัดการ ของสินทรัพย์ที่ยัง “ใช้งาน” มีปุ่ม จำหน่าย กดแล้วกรอกมูลค่าที่ขาย/จำหน่าย ระบบบันทึกวันที่และมูลค่าจำหน่าย พร้อมลงบัญชีใบสำคัญการจำหน่าย (ตัดราคาทุนและค่าเสื่อมสะสม รับรู้กำไร/ขาดทุน) สถานะเปลี่ยนเป็น “รอจำหน่าย/ปลดระวาง”',
        },
      ],
    },
    {
      id: 'recon',
      heading: '7. การกระทบยอดกับบัญชีแยกประเภท (GL)',
      blocks: [
        {
          kind: 'para',
          text: 'แท็บ กระทบยอด GL เปรียบเทียบยอดในทะเบียนกับยอดใน GL ทีละบัญชี/หมวด ถ้าผลต่างเป็น 0 ขึ้นป้ายเขียว (ตรงกัน) ถ้าไม่ตรงขึ้นผลต่างสีแดงให้ตามแก้ และมีป้ายเตือนจำนวนรายการที่ยังรอรับเข้าทะเบียน',
        },
        {
          kind: 'image',
          src: '/help/fixedasset/07-recon.png',
          caption:
            'แท็บกระทบยอด GL — ผลต่าง 0 (เขียว) เมื่อตรงกัน และสีแดงเมื่อทะเบียนไม่ตรงกับ GL',
        },
      ],
    },
    {
      id: 'import-export',
      heading: '8. การนำเข้าและส่งออกข้อมูล',
      blocks: [
        { kind: 'para', text: 'ส่งออก: ดาวน์โหลดรายการสินทรัพย์เป็น Excel ตามตัวกรองปัจจุบัน' },
        {
          kind: 'para',
          text: 'นำเข้า: สร้างสินทรัพย์ทีละหลายรายการจากไฟล์ Excel/CSV (หนึ่งแถว = สินทรัพย์หนึ่งรายการ) หน้าต่างแสดงคอลัมน์ที่ต้องกรอก (รหัสภายใน·ชื่อ·รหัสหมวด·วันที่ได้มา·ราคาทุน·อายุใช้งาน) พร้อมเทมเพลตและการตรวจความถูกต้องก่อนสร้าง',
        },
        {
          kind: 'image',
          src: '/help/fixedasset/08-import.png',
          caption: 'หน้าต่างนำเข้าสินทรัพย์ถาวร — ตารางอ้างอิงคอลัมน์ พร้อมดาวน์โหลดเทมเพลต',
        },
      ],
    },
    {
      id: 'statuses',
      heading: '9. สรุปสถานะของสินทรัพย์',
      blocks: [
        {
          kind: 'table',
          head: ['สถานะ', 'ความหมาย'],
          rows: [
            ['ใช้งาน', 'ยังใช้งานและคิดค่าเสื่อมราคาอยู่ (กดจำหน่ายได้)'],
            ['เสื่อมเต็ม', 'คิดค่าเสื่อมครบอายุแล้ว มูลค่าสุทธิเหลือเท่ามูลค่าซาก'],
            ['รอจำหน่าย', 'จำหน่าย/ปลดระวางแล้ว — ตัดออกจากการคิดค่าเสื่อม'],
          ],
        },
      ],
    },
    {
      id: 'faq',
      heading: '10. คำถามที่พบบ่อยและข้อควรระวัง',
      blocks: [
        { kind: 'para', text: 'ทำไมปุ่ม “บันทึก & ไปต่อ” กดไม่ได้?' },
        {
          kind: 'bullets',
          items: [
            { text: 'ยังไม่ได้เลือกหมวด หรือยังไม่ได้กรอก รหัสภายใน/ชื่อ/ราคาทุน' },
            { text: 'มูลค่าซากมากกว่าราคาทุน หรืออายุใช้งานไม่ถูกต้อง (ต้อง > 0)' },
          ],
        },
        { kind: 'para', text: 'รายการที่ซื้อผ่านบิล AP ไปอยู่ไหน?' },
        {
          kind: 'bullets',
          items: [
            {
              text: 'พักที่บัญชี FA_CLEARING รอรับเข้าทะเบียนที่แท็บ การเคลื่อนไหว → ปุ่ม รับเข้าทะเบียน',
            },
          ],
        },
      ],
    },
    {
      id: 'glossary',
      heading: '11. อภิธานศัพท์',
      blocks: [
        {
          kind: 'bullets',
          items: [
            { lead: 'ราคาทุน (Cost) — ', text: 'มูลค่าซื้อ/ได้มาของสินทรัพย์' },
            {
              lead: 'มูลค่าซาก (Salvage) — ',
              text: 'มูลค่าคงเหลือที่คาดว่าจะได้เมื่อสิ้นอายุการใช้งาน',
            },
            { lead: 'NBV — ', text: 'มูลค่าสุทธิตามบัญชี = ราคาทุน − ค่าเสื่อมสะสม' },
            { lead: 'เส้นตรง (Straight-line) — ', text: 'วิธีคิดค่าเสื่อมแบบเฉลี่ยเท่ากันทุกงวด' },
            {
              lead: 'FA_CLEARING — ',
              text: 'บัญชีพักสินทรัพย์ระหว่างรอรับเข้าทะเบียน (จากบิล AP / ใบสำคัญ)',
            },
          ],
        },
      ],
    },
  ],
};
